import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isNativeApp } from '../lib/native';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = (() => { const n = searchParams.get('next') || ''; return n.startsWith('/') && !n.startsWith('//') ? n : '/dashboard'; })();
  const [notice] = useState(() => {
    const n = sessionStorage.getItem('civilx_login_notice');
    if (n) sessionStorage.removeItem('civilx_login_notice');
    return n || '';
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate(result.mustChangePassword ? '/change-password' : nextPath, { replace: true });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="mb-6 flex flex-col items-center">
              <img
                src="/assets/civilx-tms-logo.png"
                alt="CivilX TMS"
                className="h-16 w-auto object-contain"
                data-testid="login-logo"
              />
              <p className="text-[10px] text-slate-500 uppercase tracking-[0.25em] font-semibold mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Dispatch · Track · Deliver</p>
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Sign In</h2>
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Access your CivilX TMS dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {notice && (
              <div data-testid="login-notice-message" className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                {notice}
              </div>
            )}
            {error && (
              <div data-testid="login-error-message" className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Email</label>
              <input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none transition-all"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                required
                disabled={loading}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Password</label>
                <a
                  href="/forgot-password"
                  data-testid="login-forgot-password-link"
                  className="text-xs font-semibold text-[#002FA7] hover:text-[#00247D] hover:underline"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  Forgot password?
                </a>
              </div>
              <input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none transition-all"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              data-testid="login-form-submit-button"
              disabled={loading}
              className="w-full bg-[#002FA7] text-white py-3 px-4 rounded-md font-semibold hover:bg-[#00247D] focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-2 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            {isNativeApp() ? (
              <p className="text-center text-xs text-slate-500 pt-2" data-testid="login-native-access-note" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Accounts are created by your company administrator. Ask your dispatcher for an invite to get access.
              </p>
            ) : (
            <p className="text-center text-xs text-slate-500 pt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              New here?{' '}
              <a
                href="/start-trial"
                data-testid="login-start-trial-link"
                className="font-semibold text-[#002FA7] hover:text-[#00247D] hover:underline"
              >
                Start your free trial
              </a>
            </p>
            )}

            <p className="text-center text-xs text-slate-400 pt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <a
                href="/privacy-policy"
                data-testid="login-privacy-policy-link"
                className="text-slate-500 hover:text-[#002FA7] hover:underline"
              >
                Privacy Policy
              </a>
              <span className="mx-2 text-slate-300">·</span>
              <a
                href="/terms"
                data-testid="login-terms-link"
                className="text-slate-500 hover:text-[#002FA7] hover:underline"
              >
                Terms of Service
              </a>
            </p>
          </form>
        </div>
      </div>

      <div
        data-testid="login-hero"
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(/images/login-hero.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-900/40 to-transparent flex items-end justify-center pb-16">
          <div className="text-center text-white p-8 max-w-md">
            <img src="/icons/logo-wordmark.png" alt="CivilX TMS" className="w-72 mx-auto mb-4 drop-shadow-2xl" />
            <p className="text-base text-slate-200" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Multi-tenant fleet &amp; dispatch management — coast to coast.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;